import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * 退会コンポーネント
 *
 * @element kd-account-delete
 * @description 退会確認ダイアログ → DeleteMe API 呼び出し → ログアウトリダイレクトを制御する。
 *
 * @attr {string} confirmation-message - 確認ダイアログのメッセージ
 * @attr {string} success-message - 退会成功時のメッセージ
 * @attr {string} error-message - エラー時のメッセージ
 * @attr {string} subscribed-error-message - 定期購入中エラーのメッセージ
 * @attr {string} redirect-url - 退会後のリダイレクト先URL
 */
@customElement('kd-account-delete')
export class KdAccountDelete extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String, attribute: 'confirmation-message' })
  confirmationMessage = '';

  @property({ type: String, attribute: 'success-message' })
  successMessage = '';

  @property({ type: String, attribute: 'error-message' })
  errorMessage = '';

  @property({ type: String, attribute: 'subscribed-error-message' })
  subscribedErrorMessage = '';

  @property({ type: String, attribute: 'redirect-url' })
  redirectUrl = '/';

  private handleDeleteClick = (e: Event): void => {
    e.preventDefault();

    const button = this.querySelector('#delete-account-button') as HTMLElement | null;
    if (!button) return;

    const { email, customerId } = button.dataset;
    if (!email || !customerId) return;

    // eslint-disable-next-line no-alert, no-restricted-globals
    if (!confirm(this.confirmationMessage)) return;

    this.executeDelete(customerId, email);
  };

  connectedCallback(): void {
    super.connectedCallback();

    const form = this.querySelector('#delete_me-button');
    if (form) {
      form.addEventListener('submit', this.handleDeleteClick);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    const form = this.querySelector('#delete_me-button');
    if (form) {
      form.removeEventListener('submit', this.handleDeleteClick);
    }
  }

  private async executeDelete(customerId: string, email: string): Promise<void> {
    try {
      const response = await fetch('/apps/deleteme/customer-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          delete_id: customerId,
          delete_email: email,
        }),
      });

      if (response.ok) {
        // eslint-disable-next-line no-alert
        alert(this.successMessage);
        await this.logout();
      } else {
        const text = await response.text();
        if (text === 'Customer Subscribed') {
          // eslint-disable-next-line no-alert
          alert(this.subscribedErrorMessage);
        } else {
          // eslint-disable-next-line no-alert
          alert(this.errorMessage);
        }
      }
    } catch {
      // eslint-disable-next-line no-alert
      alert(this.errorMessage);
    }
  }

  private async logout(): Promise<void> {
    try {
      const response = await fetch('/account/logout');
      if (response.ok) {
        window.location.href = this.redirectUrl;
      } else {
        // eslint-disable-next-line no-alert
        alert(this.errorMessage);
      }
    } catch {
      // eslint-disable-next-line no-alert
      alert(this.errorMessage);
    }
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-account-delete': KdAccountDelete;
  }
}
